import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ChevronDown,
  ChevronUp,
  Close,
  DreamhubGradient,
  ReferenceQuote,
  SquareArrowUpRight
} from "@dreamhub/designsystem/icons";
import IconContainer from "@/components/ui/icon-container";
import Text from "@/components/ui/text";
import { IconButton } from "@/components/ui/icon-button";
import { Button } from "@/components/ui/button";
import { PopoverContent } from "@/components/ui/popover";
import Skeleton from "@/components/ui/skeleton";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { DreamerSource, TaskData } from "@/entities/tasks/types";
import { formatFullDate, toCalendarDate } from "@/utils/datetime";
import { cn } from "@/utils/utils";
import {
  SourceEntity,
  useSourceActivity
} from "@/components/controls/Task/useSourceActivity";

const TRANSLATION_KEY = "tasks.drawer.task.dreamer";

const DREAMER_ICON_CLASSES =
  "rounded-lg border-[0.5px] border-border-border1 backdrop-blur-[50px]";

interface SourceRowProps {
  source: DreamerSource;
  entityType?: EntityType;
  entityId?: string;
  onViewActivity?: (activityId: string, entity: SourceEntity) => void;
  defaultOpen?: boolean;
}

const SourceRow: React.FC<SourceRowProps> = ({
  source,
  entityType,
  entityId,
  onViewActivity,
  defaultOpen = false
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [isHovered, setIsHovered] = useState(false);

  const { isReady, displayTitle, displayType, sourceEntity } =
    useSourceActivity(source, entityType, entityId);

  const handleToggle = () => setIsOpen((prev) => !prev);

  const handleViewActivity = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!source.activityId || !sourceEntity) return;
    onViewActivity?.(source.activityId, sourceEntity);
  };

  // Drilling in follows the activity's own resolved entity, not the entity
  // the row was fetched under — those diverge exactly when the task's
  // linked record isn't where the source activity actually lives.
  const canDrill = Boolean(
    sourceEntity && source.activityId && onViewActivity
  );
  const hasQuote = Boolean(source.sourceQuote);
  const isExpandable = hasQuote || canDrill;

  const showChevron = isExpandable && (isOpen || isHovered);

  // The activity is only fetched once the popover opens, so show a
  // placeholder row until every piece of this row's dynamic content (title,
  // type, drill-down button) is ready — the real row must appear as one
  // atomic unit, never with the button popping in after the rest.
  if (!isReady) {
    return (
      <div
        className="flex items-center gap-2 p-2 w-full"
        role="status"
        aria-busy="true"
        aria-label={t(`${TRANSLATION_KEY}.loadingSource`)}
      >
        <IconContainer size="sm" color="secondary">
          <ReferenceQuote />
        </IconContainer>
        <Skeleton className="flex-1 h-4" />
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex flex-col rounded-md w-full transition-colors",
        isOpen
          ? "bg-surface-focus-selected"
          : isHovered && isExpandable && "bg-surface-hover-soft"
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <button
        type="button"
        onClick={isExpandable ? handleToggle : undefined}
        disabled={!isExpandable}
        className={cn(
          "flex w-full items-center justify-between gap-2 p-2 text-left",
          isExpandable ? "cursor-pointer" : "cursor-default"
        )}
        aria-expanded={isExpandable ? isOpen : undefined}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <IconContainer size="sm" color="secondary">
            <ReferenceQuote />
          </IconContainer>
          <Text variant="smBody" className="truncate" hasTruncatedTooltip>
            {displayTitle}
            {displayType && (
              <Text as="span" variant="smBodyTertiary">
                {` (${displayType})`}
              </Text>
            )}
          </Text>
        </div>
        {showChevron && (
          <IconContainer
            size="sm"
            color="secondary"
            className={cn(
              "shrink-0 rounded-[6px] p-[2px]",
              isOpen && "bg-white-8"
            )}
          >
            {isOpen ? <ChevronUp /> : <ChevronDown />}
          </IconContainer>
        )}
      </button>
      {isExpandable && (
        <div
          className={cn(
            "grid transition-all duration-200 ease-in-out",
            isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
          )}
        >
          <div className="overflow-hidden">
            <div className="px-2 pb-2 flex flex-col gap-2">
              {hasQuote && (
                <Text
                  variant="smBody"
                  className="italic whitespace-pre-line px-2"
                >
                  {`"${source.sourceQuote}"`}
                </Text>
              )}
              {canDrill && (
                <Button
                  variant="link"
                  onClick={handleViewActivity}
                  className="group flex w-fit"
                >
                  <IconContainer
                    size="sm"
                    color="link"
                    className="group-hover:text-link-hover transition-colors"
                  >
                    <SquareArrowUpRight />
                  </IconContainer>
                  {t(
                    `${TRANSLATION_KEY}.viewActivity`,
                    { type: displayType || "" }
                  ).trim()}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface Props {
  task: TaskData;
  sources: DreamerSource[];
  entityType?: EntityType;
  entityId?: string;
  contextOwnerName?: string | null;
  // Pre-resolved by the parent — avoids a useUsers subscription inside the popover.
  assigneeName?: string | null;
  onClose: () => void;
  onViewActivity?: (activityId: string, entity: SourceEntity) => void;
}

const DreamerSourcesPopover: React.FC<Props> = ({
  task,
  sources,
  entityType,
  entityId,
  contextOwnerName,
  assigneeName,
  onClose,
  onViewActivity
}) => {
  const { t } = useTranslation();

  const headerDate = useMemo(() => {
    if (!task.createdAt) return null;
    return formatFullDate(toCalendarDate(task.createdAt), true, false);
  }, [task.createdAt]);

  const showAssignmentReason = Boolean(
    contextOwnerName &&
      assigneeName &&
      task.assigneeId &&
      task.createdBy !== task.assigneeId
  );
  const isSingleSource = sources.length === 1;

  return (
    <PopoverContent
      align="start"
      side="bottom"
      padding={16}
      className="w-[22rem] rounded-2xl flex flex-col gap-4 bg-surface-elevation1"
      onClick={(event) => event.stopPropagation()}
    >
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <IconContainer size="5" className={DREAMER_ICON_CLASSES}>
            <DreamhubGradient />
          </IconContainer>
          <Text variant="lgLabel" className="truncate">
            {t(`${TRANSLATION_KEY}.createdByDreamer`)}
          </Text>
        </div>
        {headerDate && (
          <Text variant="smBodyTertiary" className="shrink-0">
            {headerDate}
          </Text>
        )}
        <IconButton
          variant="iconNoBg"
          iconColor="secondary"
          size="sm"
          onClick={onClose}
          aria-label={t("close")}
          className="shrink-0"
        >
          <Close />
        </IconButton>
      </div>
      {showAssignmentReason && (
        <Text variant="smBodySecondary" className="px-1">
          {t(`${TRANSLATION_KEY}.assignedOnBehalf`, {
            assignee: assigneeName,
            contextOwner: contextOwnerName
          })}
        </Text>
      )}
      {sources.length > 0 && (
        <div className="flex flex-col gap-2">
          <Text variant="smBodySecondary" className="px-1">
            {t(`${TRANSLATION_KEY}.basedOnSources`, { count: sources.length })}
          </Text>
          <div className="flex flex-col">
            {sources.map((source, index) => (
              <SourceRow
                key={`${source.activityId}-${index}`}
                source={source}
                entityType={entityType}
                entityId={entityId}
                onViewActivity={onViewActivity}
                defaultOpen={isSingleSource}
              />
            ))}
          </div>
        </div>
      )}
    </PopoverContent>
  );
};

export default DreamerSourcesPopover;
